import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import teamService from '../../services/teamService';
import championshipService from '../../services/championshipService';
import './TeamEdit.css';

const TeamLogo = ({ badge, name, size = 'w-24 h-24' }) => {
  const [imgSrc, setImgSrc] = useState(badge || '/default-team-badge.png');

  useEffect(() => {
    setImgSrc(badge || '/default-team-badge.png');
  }, [badge]);

  const handleError = () => {
    setImgSrc('/default-team-badge.png');
  };

  return (
    <div className="team-logo">
      <img
        src={imgSrc}
        alt={`Logo do time ${name}`}
        onError={handleError}
        className={size}
        style={{ objectFit: 'contain', borderRadius: '8px', border: '1px solid #ccc' }}
      />
    </div>
  );
};

function TeamEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    city: '',
    foundedYear: '',
    championships: [],
    badge: null,
  });

  const [currentBadge, setCurrentBadge] = useState('');
  const [championships, setChampionships] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const team = await teamService.getById(id);
        const allChampsResponse = await championshipService.getAll();

        // --- CORREÇÃO APLICADA AQUI ---
        // Garante que estamos extraindo o array de campeonatos corretamente da resposta da API.
        // Acessa a propriedade 'data' da resposta e, em seguida, a propriedade 'data' do payload,
        // com fallbacks para garantir que seja sempre um array.
        const championshipsData = allChampsResponse.data?.data || allChampsResponse.data || [];
        if (!Array.isArray(championshipsData)) {
            console.warn("A resposta de 'getAllChampionships' não continha um array válido.", championshipsData);
        }

        const teamLogoUrl = team.badge || '/default-team-badge.png';

        setFormData({
          name: team.name || '',
          city: team.city || '',
          foundedYear: team.foundedYear || '',
          championships: team.championships?.map(c => c._id) || [],
          badge: null,
        });

        setCurrentBadge(teamLogoUrl);
        // Define o estado com um array, mesmo que a resposta falhe.
        setChampionships(Array.isArray(championshipsData) ? championshipsData : []);
      } catch (err) {
        setError('Erro ao carregar dados do time');
        console.error("Falha ao buscar dados para edição do time:", err);
      }
    }

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'badge') {
      const file = files[0];
      setFormData(prev => ({ ...prev, badge: file }));
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setCurrentBadge(previewUrl);
      }
    } else if (name === 'championships') {
      const options = e.target.options;
      const selectedIds = [];
      for (let i = 0; i < options.length; i++) {
        if (options[i].selected) selectedIds.push(options[i].value);
      }
      setFormData(prev => ({ ...prev, championships: selectedIds }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formPayload = new FormData();
      formPayload.append('name', formData.name);
      formPayload.append('city', formData.city);
      formPayload.append('foundedYear', formData.foundedYear);
      formPayload.append('championships', JSON.stringify(formData.championships));

      if (formData.badge) {
        formPayload.append('badge', formData.badge);
      }

      await teamService.updateTeam(id, formPayload);
      navigate(`/teams/${id}`);
    } catch (err) {
      setError('Erro ao salvar time');
    }
  };

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="team-edit-container max-w-3xl mx-auto p-6 bg-white rounded-xl shadow-md mt-10">
      <h2 className="text-2xl font-bold mb-6">Editar Time</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-center gap-6">
          <TeamLogo badge={currentBadge} name={formData.name} size="w-24 h-24" />

          <div>
            <label htmlFor="badge" className="block font-semibold mb-1">
              Logo do Time (PNG, JPG):
            </label>
            <input
              type="file"
              id="badge"
              name="badge"
              accept="image/png, image/jpeg"
              onChange={handleChange}
            />
          </div>
        </div>

        <div>
          <label htmlFor="name" className="block font-semibold mb-1">Nome:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="input-field"
          />
        </div>

        <div>
          <label htmlFor="city" className="block font-semibold mb-1">Cidade:</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            className="input-field"
          />
        </div>

        <div>
          <label htmlFor="foundedYear" className="block font-semibold mb-1">Ano de Fundação:</label>
          <input
            type="number"
            id="foundedYear"
            name="foundedYear"
            value={formData.foundedYear}
            onChange={handleChange}
            min="1800"
            max={new Date().getFullYear()}
            className="input-field"
          />
        </div>

        <div>
          <label htmlFor="championships" className="block font-semibold mb-1">
            Campeonatos:
          </label>
          <select
            id="championships"
            name="championships"
            multiple
            value={formData.championships}
            onChange={handleChange}
            className="input-field"
            size={Math.min(championships.length, 6)}
          >
            {championships.map(champ => (
              <option key={champ._id} value={champ._id}>
                {champ.name}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700"
        >
          Salvar
        </button>
      </form>
    </div>
  );
}

export default TeamEdit;